import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

// Hydrophobicity-based ordering (Kyte-Doolittle, hydrophobic → polar → charged)
const AA_ORDER = [
  "ILE", "VAL", "LEU", "PHE", "CYS", "MET", "ALA", "GLY",
  "THR", "SER", "TRP", "TYR", "PRO", "HIS",
  "ASN", "GLN", "ASP", "GLU", "LYS", "ARG",
];

const AAPairHeatmap = ({ contactData }) => {
  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({});

  useEffect(() => {

    if (!Array.isArray(contactData) || contactData.length === 0) {
      setPlotData([]);
      return;
    }

    // Count AA-AA pairs (unordered: pair (X,Y) == pair (Y,X), each contact counted once)
    const seenContacts = new Set();
    const pairCounts = {};

    contactData.forEach(({ details, anPosition, contactPosition }) => {
      if (!details) return;

      // Avoid double-counting symmetric contacts (i,j) and (j,i)
      const i = parseInt(anPosition, 10);
      const j = parseInt(contactPosition, 10);
      if (!isNaN(i) && !isNaN(j)) {
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (seenContacts.has(key)) return;
        seenContacts.add(key);
      }

      const parts = details.split('|');
      if (parts.length < 2) return;

      const aas = [];
      for (let k = 0; k < parts.length; k++) {
        const tokens = parts[k].trim().split('/');
        if (tokens.length >= 4) {
          aas.push(tokens[3].trim().toUpperCase());
        }
      }
      if (aas.length < 2) return;

      // Sort alphabetically so (LEU, VAL) and (VAL, LEU) collapse together
      const a = aas[0];
      const b = aas[1];
      const pairKey = a < b ? `${a}|${b}` : `${b}|${a}`;
      pairCounts[pairKey] = (pairCounts[pairKey] || 0) + 1;
    });

    // Build a symmetric matrix in AA_ORDER
    const observedAAs = new Set();
    Object.keys(pairCounts).forEach((k) => {
      const [a, b] = k.split('|');
      observedAAs.add(a);
      observedAAs.add(b);
    });

    const labels = AA_ORDER.filter((aa) => observedAAs.has(aa));
    const z = labels.map((rowAA) =>
      labels.map((colAA) => {
        const key = rowAA < colAA ? `${rowAA}|${colAA}` : `${colAA}|${rowAA}`;
        return pairCounts[key] || 0;
      })
    );

    setPlotData([
      {
        x: labels,
        y: labels,
        z: z,
        type: "heatmap",
        hovertemplate: "%{y} – %{x}<br>Count: %{z}<extra></extra>",
        colorscale: [
          [0, '#ffffff'],
          [1, '#58c5bf'],
        ],
        showscale: true,
        colorbar: {
          title: { text: "Count" },
        },
      },
    ]);

    setLayout({
      autosize: true,
       font: { size: 9 },
      title: {
        text: "Amino Acid Pair Contacts",
      },
      xaxis: {
        type: "category",
        title: { text: "Amino acid" },
      },
      yaxis: {
        type: "category",
        title: { text: "Amino acid" },
        autorange: "reversed",
      },
      margin: { t: 50, b: 80, l: 80, r: 40 },
    });
  }, [contactData]);

  return (
    <div className="aspect-square card">
      <Plot
        data={plotData}
        layout={layout}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default AAPairHeatmap;
